import React from 'react';
import { Col, Collapse, Row } from 'antd';
import {
    AddressResponse,
    HospiceLocationResponse,
    HospiceOrganizationWithDetailsResponse,
    HospiceTeamResponse,
    PlaceOfServiceResponse,
} from '../../models/OrganizationModels';
import RoundedButton from '../RoundedButton';
import { useNavigate } from 'react-router-dom';
import '../../styles/_orgs.scss';
import PlusButton from '../PlusButton';
import OrganizationHeader from './OrganizationHeader';
import RoundedButtonWithBorder from '../RoundedButtonWithBorder';
import { Utility } from '../../utilities/Utility';
import { EmrPersistenceTypeLabelMap } from '../../models/EmrModels';
import { EditOutlined } from '@ant-design/icons';

const { Panel } = Collapse;

type Props = {
    data: HospiceOrganizationWithDetailsResponse[];
    openModal: Function;
    setOpenNewOrgModal: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedLocation: React.Dispatch<React.SetStateAction<string>>;
    setOpenLocationModal: Function;
    setSelectedOrganization: React.Dispatch<React.SetStateAction<string>>;
    setSelectedLocationId: React.Dispatch<React.SetStateAction<string>>;
    setOpenPlaceOfServiceModal: Function;
    setTeamName: React.Dispatch<React.SetStateAction<string>>;
    setOrgId: React.Dispatch<React.SetStateAction<string>>;
    setTeamId: React.Dispatch<React.SetStateAction<string>>;
    setSelectedPlaceOfService: React.Dispatch<
        React.SetStateAction<PlaceOfServiceResponse | null>
    >;
    setSelectedTeam: React.Dispatch<
        React.SetStateAction<HospiceTeamResponse | null>
    >;
    setSelectedLocationItem: React.Dispatch<
        React.SetStateAction<HospiceLocationResponse | null>
    >;
    setSelectedOrganizationItem: React.Dispatch<
        React.SetStateAction<HospiceOrganizationWithDetailsResponse | null>
    >;
};

const OrganizationCollapseView: React.FC<Props> = ({
    data,
    openModal,
    setOpenNewOrgModal,
    setSelectedLocation,
    setOpenLocationModal,
    setSelectedOrganization,
    setSelectedLocationId,
    setTeamName,
    setOpenPlaceOfServiceModal,
    setOrgId,
    setTeamId,
    setSelectedPlaceOfService,
    setSelectedTeam,
    setSelectedLocationItem,
    setSelectedOrganizationItem,
}) => {
    const navigate = useNavigate();
    
    const addressFieldText = (address: AddressResponse) => {
        if (address === undefined) return 'N/A';
        
        let addressText = [
            address?.streetAddress,
            address?.streetAddress2,
            address?.city,
            address?.state,
            address?.zipCode,
            address?.country
        ].filter(part => !Utility.isNullOrUndefinedOrEmpty(part)).join(', ');
        
        return Utility.isNullOrUndefinedOrEmpty(addressText) ? "N/A" : addressText;
    }

    const defaultPcpName = (team: HospiceTeamResponse) => {
        const defaultPcp = team.teamUsers?.find((user) => user.isDefaultPcp);

        return Utility.isNullOrUndefinedOrEmpty(defaultPcp?.fullName)
            ? 'N/A'
            : defaultPcp?.fullName;
    };

    return (
        <Collapse className="bg-transparent organization-collapse">
            {data.map((organization) => (
                <Panel
                    showArrow={true}
                    className="mb-2 bg-collapse-light-blue"
                    key={organization.id}
                    header={
                        <div className="d-flex align-items-center justify-content-between">
                            <OrganizationHeader
                                headerText={`Organization: ${organization.hospiceName}`}
                            />
                            <div>
                                <RoundedButton
                                    title="Edit"
                                    className="bg-transparent shadow-none text-black me-2"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedOrganizationItem(
                                            organization,
                                        );
                                        setOpenNewOrgModal(true);
                                    }}
                                >
                                    <EditOutlined rev={undefined} />
                                </RoundedButton>
                                <RoundedButtonWithBorder
                                    className="br-medium-blue text-medium-blue bg-white me-2"
                                    border="5px"
                                    onClick={() =>
                                        navigate(
                                            `${organization.id}/order-sets?orgName=${organization.hospiceName}`,
                                        )
                                    }
                                >
                                    Comfort Kits/Order Sets
                                </RoundedButtonWithBorder>
                                <RoundedButton
                                    className="bg-medium-blue text-white"
                                    onClick={() =>
                                        navigate(`${organization.id}/users`, {
                                            state: organization,
                                        })
                                    }
                                >
                                    Manage Users
                                </RoundedButton>
                            </div>
                        </div>
                    }
                >
                    <div>
                        <div className="d-flex justify-content-end">
                            <PlusButton
                                className="mb-2"
                                onClick={() => {
                                    setSelectedOrganization(
                                        organization.hospiceName,
                                    );
                                    setOrgId(organization.id);
                                    setSelectedLocationItem(null);
                                    setOpenLocationModal(true);
                                }}
                            >
                                New Location
                            </PlusButton>
                        </div>

                        <Row>
                            <Col span={4} className="p-3 fs-7 bg-grey border border-dark-subtle">
                                Address
                            </Col>
                            <Col span={20} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                {addressFieldText(organization.address)}
                            </Col>
                        </Row>

                        <Row>
                            <Col span={4} className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle">
                                Phone
                            </Col>
                            <Col span={4} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                {organization.phoneNumber ?? "N/A"}
                            </Col>
                            <Col span={4} className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle">
                                Email
                            </Col>
                            <Col span={4} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                {organization.adminEmailAddress ?? "N/A"}
                            </Col>
                            <Col span={4} className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle">
                                Organization Code
                            </Col>
                            <Col span={4} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                {organization.hospiceRefId}
                            </Col>
                        </Row>

                        <Row>
                            <Col span={4} className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle">
                                Effective Date
                            </Col>
                            <Col span={8} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                {Utility.getFormattedDate(organization.effectiveDate)}
                            </Col>

                            <Col span={4} className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle">
                                Implementation Date
                            </Col>
                            <Col span={8} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                {Utility.getFormattedDate(organization.implementationDate)}
                            </Col>
                        </Row>
                        
                        {
                            organization.emrDetails &&

                            <Row>
                                <Col span={4} className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle">
                                    EMR Name
                                </Col>
                                <Col span={8} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                    {organization.emrDetails.name}
                                </Col>

                                <Col span={4} className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle">
                                    Persistence Type
                                </Col>
                                <Col span={8} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                    {EmrPersistenceTypeLabelMap[organization.emrDetails.persistenceType]}
                                </Col>
                            </Row>
                        }   
                        
                    </div>

                    {organization.hospiceLocations.map((location) => (
                        <Collapse
                            key={location.id}
                            bordered={false}
                            className="custom-collapse"
                        >
                            <Collapse.Panel
                                header={
                                    <div className="d-flex align-items-center justify-content-between">
                                        <OrganizationHeader
                                            headerText={`Location: ${location.description}`}
                                        />
                                        <RoundedButton
                                            title="Edit"
                                            className="bg-transparent shadow-none text-black"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedOrganization(
                                                    organization.hospiceName,
                                                );
                                                setOrgId(organization.id);
                                                setSelectedLocationItem(location);
                                                setOpenLocationModal(true);
                                            }}
                                        >
                                            <EditOutlined rev={undefined} />
                                        </RoundedButton>
                                    </div>
                                }
                                key={location.id}
                                className="mb-2 bg-collapse-light-blue"
                            >
                                <div className="d-flex justify-content-end">
                                    <PlusButton
                                        className="mb-2"
                                        onClick={() => {
                                            setSelectedLocation(
                                                location.description,
                                            );
                                            setSelectedLocationId(location.id);
                                            setSelectedTeam(null);
                                            openModal(true);
                                        }}
                                    >
                                        New Team
                                    </PlusButton>
                                </div>

                                <Row className="mb-2">
                                    <Col span={4} className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle">
                                        {' '}
                                        Location ID{' '}
                                    </Col>
                                    <Col span={8} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                        {location.rxLogicId}
                                    </Col>

                                    <Col span={4} className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle">
                                        Location Code
                                    </Col>
                                    <Col span={8} className="bg-white p-3 fs-7 bg-grey border border-dark-subtle">
                                        {location.code}
                                    </Col>
                                </Row>

                                {location.hospiceTeams.map((team) => (
                                    <Collapse
                                        bordered={false}
                                        key={team.id}
                                        className="custom-collapse"
                                    >
                                        <Collapse.Panel
                                            header={
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <OrganizationHeader
                                                        headerText={`Team: ${team.teamName}`}
                                                    />
                                                    <RoundedButton
                                                        title="Edit"
                                                        className="bg-transparent shadow-none text-black"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedLocation(
                                                                location.description,
                                                            );
                                                            setSelectedLocationId(
                                                                location.id,
                                                            );
                                                            setSelectedTeam(team);
                                                            openModal(true);
                                                        }}
                                                    >
                                                        <EditOutlined rev={undefined} />
                                                    </RoundedButton>
                                                </div>
                                            }
                                            key={team.id}
                                            className="mb-2 bg-collapse-light-blue"
                                        >
                                            <div className="d-flex justify-content-end">
                                                <PlusButton
                                                    className="mb-2"
                                                    onClick={() => {
                                                        setTeamName(
                                                            team.teamName,
                                                        );
                                                        setTeamId(team.id);
                                                        setSelectedPlaceOfService(
                                                            null,
                                                        );
                                                        setOpenPlaceOfServiceModal(
                                                            true,
                                                        );
                                                    }}
                                                >
                                                    New Place of Service
                                                </PlusButton>
                                            </div>
                                            
                                            <Row>
                                                <Col span={4} className="p-3 fs-7 bg-grey border border-dark-subtle">
                                                    Address
                                                </Col>
                                                <Col span={20} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                                    {addressFieldText(team.address)}
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col span={4} className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle">
                                                    Phone
                                                </Col>
                                                <Col span={8} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                                    {team.phone ?? "N/A"}
                                                </Col>
                                                <Col span={4} className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle">
                                                    Email
                                                </Col>
                                                <Col span={8} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                                    {team.email ?? "N/A"}
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col
                                                    xs={24}
                                                    md={4}
                                                    className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle"
                                                >
                                                    RxLogic's Team ID
                                                </Col>
                                                <Col
                                                    xs={24}
                                                    md={4}
                                                    className="p-3 fs-7 bg-white bg-grey border border-dark-subtle"
                                                >
                                                    {team.rxLogicId}
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={4}
                                                    className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle"
                                                >
                                                    ScriptSure's Team ID
                                                </Col>
                                                <Col
                                                    xs={24}
                                                    md={4}
                                                    className="p-3 fs-7 bg-white bg-grey border border-dark-subtle"
                                                >
                                                    {team.scriptSureId}
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={4}
                                                    className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle"
                                                >
                                                    RxLogic's Team Code
                                                </Col>
                                                <Col
                                                    xs={24}
                                                    md={4}
                                                    className="p-3 fs-7 bg-white bg-grey border border-dark-subtle"
                                                >
                                                    {team.code}
                                                </Col>
                                            </Row>

                                            <Row className="mb-2">
                                                <Col span={4} className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle">
                                                    Default PCP
                                                </Col>
                                                <Col span={20} className="p-3 fs-7 bg-white bg-grey border border-dark-subtle">
                                                    {defaultPcpName(team)}
                                                </Col>
                                            </Row>

                                            {team.placeOfServices.map(
                                                (service) => (
                                                    <Collapse
                                                        bordered={false}
                                                        key={service.id}
                                                        className="custom-collapse"
                                                    >
                                                        <Collapse.Panel
                                                            header={
                                                                <div className="d-flex align-items-center justify-content-between">
                                                                    <OrganizationHeader
                                                                        headerText={`Place of Service: ${service.name.toUpperCase()}`}
                                                                    />
                                                                    <RoundedButton
                                                                        title="Edit"
                                                                        className="bg-transparent shadow-none text-black"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setTeamName(
                                                                                team.teamName,
                                                                            );
                                                                            setTeamId(
                                                                                team.id,
                                                                            );
                                                                            setSelectedPlaceOfService(
                                                                                service,
                                                                            );
                                                                            setOpenPlaceOfServiceModal(
                                                                                true,
                                                                            );
                                                                        }}
                                                                    >
                                                                        <EditOutlined rev={undefined} />
                                                                    </RoundedButton>
                                                                </div>
                                                            }
                                                            key={service.id}
                                                            className="mb-2 bg-collapse-light-blue"
                                                        >
                                                            <Row className="mb-2">
                                                                <Col
                                                                    span={5}
                                                                    className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle"
                                                                >
                                                                    Place of
                                                                    Service ID
                                                                </Col>
                                                                <Col
                                                                    span={7}
                                                                    className="p-3 fs-7 bg-white bg-grey border border-dark-subtle"
                                                                >
                                                                    {
                                                                        service.rxLogicId
                                                                    }
                                                                </Col>

                                                                <Col
                                                                    span={5}
                                                                    className="p-3 fs-7 bg-grey bg-grey border border-dark-subtle"
                                                                >
                                                                    Place of
                                                                    Service Code
                                                                </Col>
                                                                <Col
                                                                    span={7}
                                                                    className="p-3 fs-7 bg-white bg-grey border border-dark-subtle"
                                                                >
                                                                    {
                                                                        service.code
                                                                    }
                                                                </Col>
                                                            </Row>
                                                        </Collapse.Panel>
                                                    </Collapse>
                                                ),
                                            )}
                                        </Collapse.Panel>
                                    </Collapse>
                                ))}
                            </Collapse.Panel>
                        </Collapse>
                    ))}
                </Panel>
            ))}
        </Collapse>
    );
};

export default OrganizationCollapseView;
